<template>
	<VerticalLayout size="middle">
		<Skeleton :paragraph="{ rows: 1 }" :title="false" :loading="loading">
			<HorizontalLayout v-if="filterOptions.length > 0" size="middle" :direction="buttonsDirection">
				<TypographyText v-if="status.maxRtcmfMountpointsCount === 1">
					<Space v-if="filterOptions.length > 0">
						{{ $t('settings.vrsSettingsForm.rtcmMessagesFilters.fullMountpointLabel') }}
						<TypographyText strong>{{ filterOptions[0].label }}</TypographyText>
						<Button class="pd-lg-left" :disabled="disabled" @click="onRenameRtcmfMountpointClick">
							{{ $t('settings.vrsSettingsForm.rtcmMessagesFilters.renameButtonLabel') }}
						</Button>
					</Space>
					<span v-else>  </span>
				</TypographyText>
				<Select v-else
					class="ui-select"
					v-model:value="filterSelectedId"
					:options="filterOptions"
					:disabled="disabled"
					option-label-prop="label"
				/>
				<HorizontalLayout v-if="status.maxRtcmfMountpointsCount !== 1">
					<Button
						class="width-100"
						type="primary"
						:disabled="disabled || (status.maxRtcmfMountpointsCount > 0 && filterOptions.length >= status.maxRtcmfMountpointsCount)"
						@click="onAddRtcmfMountpointClick"
					>
						<PlusCircleOutlined />{{ $t('settings.vrsSettingsForm.rtcmMessagesFilters.addButtonLabel') }}
					</Button>
					<Button class="width-100" :disabled="disabled" @click="onRenameRtcmfMountpointClick">
						{{ $t('settings.vrsSettingsForm.rtcmMessagesFilters.renameButtonLabel') }}
					</Button>
					<PopconfirmButton
						:title="$t('settings.vrsSettingsForm.rtcmMessagesFilters.deleteConfirmation')"
						:ok-text="$t('settings.vrsSettingsForm.rtcmMessagesFilters.deleteButtonLabel')"
						:cancel-text="$t('settings.vrsSettingsForm.rtcmMessagesFilters.cancelButtonLabel')"
						:disabled="disabled || filterOptions.length <= 1"
						:onConfirm="onDeleteRtcmfMountpointConfirm"
						placement="topRight"
						buttonClass="width-100"
					>
						{{ $t('settings.vrsSettingsForm.rtcmMessagesFilters.deleteButtonLabel') }}
					</PopconfirmButton>
				</HorizontalLayout>
			</HorizontalLayout>
			<VerticalLayout v-else>
				<TypographyParagraph>
					{{ $t('settings.vrsSettingsForm.rtcmMessagesFilters.description') }}
				</TypographyParagraph>
				<Button type="primary" :disabled="disabled" @click="onAddRtcmfMountpointClick">
					<PlusCircleOutlined />{{ $t('settings.vrsSettingsForm.rtcmMessagesFilters.addButtonLabel') }}
				</Button>
			</VerticalLayout>
		</Skeleton>
		<RtcmMessagesFiltersForm
			v-if="filterOptions.length > 0"
			:loading="loading"
			:status="status"
			:settings="settings"
			:rtcmFilters="rtcmFilters"
			:isVrs="isVrs"
			:fetchSettings="fetchSettings"
			:disabled="disabled"
		/>
	</VerticalLayout>

	<RtcmMessagesFiltersAddRenameModal
		v-model="addRenamePopupVisible"
		:addMode="addMode"
		:mountpointId="filterSelectedId"
		:mountpointLabel="selectedMountpointLabel"
		:baseMountpoint="baseMountpoint"
		:networkId="networkId"
		:onSuccess="onRtcmfMountpointDone"
		:fetchSettings="fetchSettings"
	/>
</template>

<script>
import { ref, computed, toRef, watch } from 'vue';
import { Space, Select, Button, Skeleton, TypographyParagraph, TypographyText, message } from 'ant-design-vue';
import { PlusCircleOutlined } from '@ant-design/icons-vue';
import { useSmallScreen } from '@/utils/useSmallScreen';

import { useI18n } from 'vue-i18n';

import { showErrorMessage } from '@/components/common/errorMessage';

import Api from '@/api/Api';

import VerticalLayout from '@/components/common/VerticalLayout';
import PopconfirmButton from '@/components/common/PopconfirmButton.vue';
import HorizontalLayout from '@/components/common/HorizontalLayout.vue';

import RtcmMessagesFiltersForm from '@/components/settings/rtcmFilters/RtcmMessagesFiltersForm';
import RtcmMessagesFiltersAddRenameModal from '@/components/settings/rtcmFilters/RtcmMessagesFiltersAddRenameModal';

export default {
	components: {
		Space, Select, Button, PlusCircleOutlined, Skeleton, TypographyParagraph, TypographyText,
		RtcmMessagesFiltersForm,
		VerticalLayout, PopconfirmButton, HorizontalLayout,
		RtcmMessagesFiltersAddRenameModal,
	},
	props: {
		loading: {},
		status: {},
		settings: {},
		fetchSettings: {},
		isVrs: {},
		disabled: {
			type: Boolean,
			default: false,
		},
	},
	setup(props) {
		const { t } = useI18n();
		const settingsRef = toRef(props, 'settings');
		const isVrsRef = toRef(props, 'isVrs');
		const filterSelectedId = ref(null);
		const baseMountpoint = computed(() => {
			if (settingsRef.value == null) {
				return '';
			}
			return isVrsRef.value ? settingsRef.value.finalNameVrs : settingsRef.value.nameNear;
		});
		const networkId = computed(() => {
			if (settingsRef.value == null) {
				return 0;
			}
			return isVrsRef.value ? settingsRef.value.id : 0;
		});
		const filterOptions = computed(() => {
			if (settingsRef.value != null && settingsRef.value.rtcmfMountpoints != null) {
				return settingsRef.value.rtcmfMountpoints.map(item => ({
					value: item.id,
					label: baseMountpoint.value + '_' + item.label,
				}));
			}
			return [];
		});
		watch(filterOptions, items => {
			if (items.length > 0 && !items.some(item => item.value === filterSelectedId.value)) {
				filterSelectedId.value = items[0].value;
			}
		});
		const { smallScreen } = useSmallScreen(780);
		const buttonsDirection = computed(() => smallScreen.value ? 'vertical' : 'horizontal');

		const addRenamePopupVisible = ref(false);
		const addMode = ref(false);
		const onAddRtcmfMountpointClick = () => {
			addRenamePopupVisible.value = true;
			addMode.value = true;
		};
		const onRenameRtcmfMountpointClick = () => {
			addRenamePopupVisible.value = true;
			addMode.value = false;
		};
		const onRtcmfMountpointDone = id => {
			filterSelectedId.value = id;
			props.fetchSettings();
		};
		const onDeleteRtcmfMountpointConfirm = () => {
			Api.rtcmfMountpointDelete(filterSelectedId.value, isVrsRef.value, response => {
				if (response.status === 'ok') {
					filterSelectedId.value = null;
					message.success(t('settings.vrsSettingsForm.rtcmMessagesFilters.deleteSuccess'));
					props.fetchSettings();
				} else {
					showErrorMessage(response.errors);
				}
			});
		};

		const rtcmFilters = computed(() => {
			if (settingsRef.value != null && settingsRef.value.rtcmfMountpoints != null) {
				const rtcmFilters = settingsRef.value.rtcmfMountpoints.find(item => item.id === filterSelectedId.value);
				if (rtcmFilters != null) {
					return rtcmFilters;
				}
			}
			return {
				id: 0,
				rtcm_messages_filters: [],
			};
		});

		const selectedMountpointLabel = computed(() => {
			if (settingsRef.value != null && settingsRef.value.rtcmfMountpoints != null) {
				const item = settingsRef.value.rtcmfMountpoints.find(item => item.id === filterSelectedId.value);
				if (item != null) {
					return item.label;
				}
			}
			return '';
		});
		return {
			filterSelectedId,
			filterOptions,
			selectedMountpointLabel,
			rtcmFilters,
			buttonsDirection,

			addRenamePopupVisible,
			addMode,
			baseMountpoint,
			networkId,
			onAddRtcmfMountpointClick,
			onRenameRtcmfMountpointClick,
			onRtcmfMountpointDone,
			onDeleteRtcmfMountpointConfirm,
		}
	},
}
</script>

<style scoped>
.ui-select {
	width: clamp(100px, 30vw, 400px);
}
@media (max-width: 900px) {
	.ui-select {
		width: 100%;
	}
}
</style>
